import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faUpload, 
  faImage,
  faTrash,
  faPlus,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { addProduct, updateProduct, getProducts } from '../../services/productService';
import { uploadProductImageToStorage, deleteInventoryImage } from '../../services/inventoryService';

const ProductForm = ({ product, categories, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    categoryId: '',
    location: 'Angola',
    featured: false,
    popular: false,
    isAddon: false,
    dimensions: '',
    ageRange: '',
    capacity: '',
    features: [],
    rentalOptions: [
      { duration: 'Day', price: '' },
      { duration: 'Weekend', price: '' },
      { duration: 'Week', price: '' }
    ],
    images: [],
    details: {
      size: '',
      color: '',
      material: '',
      condition: 'New'
    }
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [newFeature, setNewFeature] = useState('');
  
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        stock: product.stock || '',
        categoryId: product.categoryId || '',
        location: product.location || 'Angola',
        featured: product.featured || false,
        popular: product.popular || false,
        isAddon: product.isAddon || false,
        dimensions: product.dimensions || '',
        ageRange: product.ageRange || '',
        capacity: product.capacity || '',
        features: product.features || [],
        rentalOptions: product.rentalOptions?.length > 0
          ? product.rentalOptions
          : [
              { duration: 'Day', price: product.price || '' },
              { duration: 'Weekend', price: '' },
              { duration: 'Week', price: '' }
            ],
        images: product.images || [],
        details: {
          size: product.details?.size || '',
          color: product.details?.color || '',
          material: product.details?.material || '',
          condition: product.details?.condition || 'New'
        }
      });
      
      if (product.images?.length > 0) {
        setImagePreviews(product.images.map(img => img.url));
      } else if (product.imageUrl) {
        setImagePreviews([product.imageUrl]);
        setFormData(prev => ({
          ...prev,
          images: [{ url: product.imageUrl, path: product.imagePath || '' }]
        }));
      }
    } else if (categories.length > 0) {
      setFormData(prev => ({
        ...prev,
        categoryId: categories[0].id
      }));
    }
  }, [product, categories]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleRentalOptionChange = (index, value) => {
    const updated = [...formData.rentalOptions];
    updated[index] = { ...updated[index], price: value };
    setFormData(prev => ({ ...prev, rentalOptions: updated }));
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };
  
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, image: 'Only JPEG, PNG, GIF, WEBP allowed.' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Max 5MB per image.' }));
        return;
      }
    }

    setUploadingImage(true);
    setErrors(prev => ({ ...prev, image: null }));
    
    try {
      const productId = product?.id || `new-${Date.now()}`;
      const uploaded = [];

      for (const file of files) {
        const result = await uploadProductImageToStorage(file, productId);
        uploaded.push(result);

        const reader = new FileReader();
        reader.onload = () => {
          setImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploaded]
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrors(prev => ({ ...prev, image: 'Upload failed. Please try again.' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async (index) => {
    const image = formData.images[index];
    if (image?.storagePath) {
      try {
        await deleteInventoryImage(image.storagePath);
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price required';
    }
    if (!formData.stock || isNaN(formData.stock) || parseInt(formData.stock) < 0) {
      newErrors.stock = 'Valid stock required';
    }
    if (!formData.categoryId) newErrors.categoryId = 'Select a category';
    if (!product && formData.images.length === 0) {
      newErrors.image = 'Upload at least one image';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        categoryId: formData.categoryId,
        location: formData.location,
        featured: formData.featured,
        popular: formData.popular,
        isAddon: formData.isAddon,
        dimensions: formData.dimensions,
        ageRange: formData.ageRange,
        capacity: formData.capacity,
        features: formData.features,
        images: formData.images,
        details: formData.details,
        rentalOptions: formData.rentalOptions
          .filter(opt => opt.price)
          .map(opt => ({ ...opt, price: parseFloat(opt.price) }))
      };

      if (formData.images.length > 0) {
        productData.imageUrl = formData.images[0].url;
        productData.imagePath = formData.images[0].storagePath || formData.images[0].path || '';
      }
      
      if (product) {
        await updateProduct(product.id, productData);
      } else {
        await addProduct(productData);
      }
      
      const updatedProducts = await getProducts();
      onSubmit(updatedProducts);
    } catch (error) {
      console.error('Error saving product:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to save product.' }));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="product-form-container">
      <div className="section-header">
        <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
        <div className="header-actions">
          <button className="secondary-button" onClick={onCancel} disabled={loading}>
            <FontAwesomeIcon icon={faTimes} /> Cancel
          </button>
          <button className="primary-button" onClick={handleSubmit} disabled={loading}>
            <FontAwesomeIcon icon={loading ? faSpinner : faSave} spin={loading} />
            {loading ? ' Saving...' : ' Save Product'}
          </button>
        </div>
      </div>
      
      {errors.submit && <div className="form-error-message">{errors.submit}</div>}

      <form className="product-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Product Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <div className="error-message">{errors.name}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="categoryId">Category *</label>
            <select
              id="categoryId"
              name="categoryId"
              value={formData.categoryId}
              onChange={handleChange}
              className={errors.categoryId ? 'error' : ''}
            >
              <option value="">Select Category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            {errors.categoryId && <div className="error-message">{errors.categoryId}</div>}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="price">Price ($/day) *</label>
            <input
              type="number"
              id="price"
              name="price"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={handleChange}
              className={errors.price ? 'error' : ''}
            />
            {errors.price && <div className="error-message">{errors.price}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="stock">Stock Quantity *</label>
            <input
              type="number"
              id="stock"
              name="stock"
              min="0"
              value={formData.stock}
              onChange={handleChange}
              className={errors.stock ? 'error' : ''}
            />
            {errors.stock && <div className="error-message">{errors.stock}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="location">Location *</label>
            <select
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
            >
              <option value="Angola">Angola</option>
              <option value="Elkhart">Elkhart</option>
              <option value="Both">Both Locations</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="description">Description *</label>
          <textarea
            id="description"
            name="description"
            rows="4"
            value={formData.description}
            onChange={handleChange}
            className={errors.description ? 'error' : ''}
          ></textarea>
          {errors.description && <div className="error-message">{errors.description}</div>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dimensions">Dimensions</label>
            <input
              type="text"
              id="dimensions"
              name="dimensions"
              value={formData.dimensions}
              onChange={handleChange}
              placeholder="e.g. 15' x 15' x 15'"
            />
          </div>
          <div className="form-group">
            <label htmlFor="ageRange">Age Range</label>
            <input
              type="text"
              id="ageRange"
              name="ageRange"
              value={formData.ageRange}
              onChange={handleChange}
              placeholder="e.g. Ages 3-12"
            />
          </div>
          <div className="form-group">
            <label htmlFor="capacity">Capacity</label>
            <input
              type="text"
              id="capacity"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              placeholder="e.g. Up to 8 kids"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="details.condition">Condition</label>
            <select
              id="details.condition"
              name="details.condition"
              value={formData.details.condition}
              onChange={handleChange}
            >
              <option value="New">New</option>
              <option value="Like New">Like New</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="details.size">Size</label>
            <input
              type="text"
              id="details.size"
              name="details.size"
              value={formData.details.size}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="form-row" style={{ gap: '2rem' }}>
          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" name="featured" checked={formData.featured} onChange={handleChange} />
              Featured Product
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" name="popular" checked={formData.popular} onChange={handleChange} />
              Popular
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" name="isAddon" checked={formData.isAddon} onChange={handleChange} />
              Add-on Item
            </label>
          </div>
        </div>

        <h3 style={{ fontSize: '1rem', color: '#1E88E5', borderBottom: '2px solid #e3f2fd', paddingBottom: '0.5rem', marginTop: '1.5rem' }}>
          Rental Pricing
        </h3>
        <div className="form-row">
          {formData.rentalOptions.map((option, index) => (
            <div className="form-group" key={index}>
              <label>{option.duration} Rate ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={option.price}
                onChange={(e) => handleRentalOptionChange(index, e.target.value)}
                placeholder="0.00"
              />
            </div>
          ))}
        </div>

        <h3 style={{ fontSize: '1rem', color: '#1E88E5', borderBottom: '2px solid #e3f2fd', paddingBottom: '0.5rem', marginTop: '1.5rem' }}>
          Features
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {formData.features.map((feature, index) => (
            <span key={index} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.35rem 0.75rem', background: '#e3f2fd', color: '#1565c0',
              borderRadius: '20px', fontSize: '0.85rem', fontWeight: 500
            }}>
              {feature}
              <button type="button" onClick={() => handleRemoveFeature(index)} style={{
                background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer', padding: 0
              }}>
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
              </button>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            placeholder="Add a feature..."
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFeature(); } }}
            style={{ flex: 1 }}
          />
          <button type="button" className="secondary-button" onClick={handleAddFeature}>
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
        
        <h3 style={{ fontSize: '1rem', color: '#1E88E5', borderBottom: '2px solid #e3f2fd', paddingBottom: '0.5rem', marginTop: '1.5rem' }}>
          Product Images
        </h3>
        <div className="image-upload-group">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '1rem',
            marginBottom: '0.5rem'
          }}>
            {formData.images.map((img, index) => (
              <div key={index} style={{
                position: 'relative', width: '100%', height: '120px',
                borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0'
              }}>
                <img src={img.url || imagePreviews[index]} alt={`Product ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" onClick={() => handleRemoveImage(index)} style={{
                  position: 'absolute', top: '4px', right: '4px',
                  background: 'rgba(211,47,47,0.9)', color: 'white', border: 'none',
                  borderRadius: '50%', width: '24px', height: '24px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: '0.7rem'
                }}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
            <label htmlFor="product-image-upload" style={{
              width: '100%', height: '120px', border: '2px dashed #1E88E5',
              borderRadius: '8px', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              cursor: 'pointer', color: '#1E88E5', fontSize: '0.85rem', background: '#f8fbff'
            }}>
              {uploadingImage ? (
                <FontAwesomeIcon icon={faSpinner} spin size="lg" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} size="lg" />
                  <span>Upload</span>
                </>
              )}
            </label>
            <input
              type="file"
              id="product-image-upload"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              disabled={uploadingImage}
            />
          </div>
          {errors.image && <div className="error-message">{errors.image}</div>}
          <p className="image-help-text">
            Images are uploaded to Firebase Storage. Max 5MB each.
          </p>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
